import React, { useState, useEffect, useContext } from "react"
import { Layout, ClientOnly } from "../components/common"
import { MetaData as MetaTags } from "../components/common/meta"
import { SubscriptionButton } from "../components/subscription/SubscriptionButton"
import plans from "../../functions/plans"
import FirebaseProvider, {
    FirebaseContext,
} from "../components/idealline/Firebase"

const SubscriptionStatusChecker = ({ onStatusChange }) => {
    const auth = useContext(FirebaseContext)
    const user = auth?.user

    useEffect(() => {
        const checkStatus = async () => {
            if (!user || user.isAnonymous) {
                onStatusChange(null)
                return
            }

            try {
                const token = await user.getIdTokenResult()
                onStatusChange(
                    token.claims?.stripeRole === "paddock_pass"
                        ? "active"
                        : "free"
                )
            } catch (error) {
                console.error("Error checking subscription:", error)
                onStatusChange(null)
            }
        }

        checkStatus()
    }, [user, onStatusChange])

    return null
}

const CheckIcon = () => (
    <svg
        className="w-5 h-5 text-green-600 mr-3 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
    >
        <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
        />
    </svg>
)

const PricingPage = ({ location }) => {
    const [billingPeriod, setBillingPeriod] = useState("yearly")
    const [contextMsg, setContextMsg] = useState(null)
    const [checkoutIntent, setCheckoutIntent] = useState("pricing")
    const [subscriptionStatus, setSubscriptionStatus] = useState(null)

    useEffect(() => {
        try {
            const query = new URLSearchParams(location?.search || "")
            const from = query.get("from")
            const reason = query.get("reason")
            const track = query.get("track")
            const intent = query.get("intent")

            setCheckoutIntent(
                intent === "track_picker" ? "track_picker" : "pricing"
            )

            if (from === "modal_create") {
                if (reason === "no_entitlement") {
                    setContextMsg(
                        `You chose to create a racing line${
                            track ? ` for ${track}` : ""
                        }. Paddock Pass is required before the line is created.`
                    )
                } else if (reason === "unauthenticated") {
                    setContextMsg(
                        "Sign in, then subscribe to Paddock Pass to create your racing line."
                    )
                } else {
                    setContextMsg(
                        "Paddock Pass is required for this creation tool. Subscribe to continue."
                    )
                }
            }
        } catch (_) {}
    }, [location?.search])

    const features = [
        "Create and save up to 50 racing lines",
        "Import iRacing telemetry data",
        "Import GoPro video telemetry",
        "Import Alfano data logger files",
        "Unlimited community racing line viewing",
        "Advanced line analysis tools",
        "Export racing lines as images",
        "Private line sharing",
        "Early access to new features",
        "No advertisements",
    ]

    const isYearly = billingPeriod === "yearly"
    const monthlyEquivalent = (plans.paddockPass.yearPrice / 12).toFixed(2)
    const displayPrice = isYearly
        ? monthlyEquivalent
        : plans.paddockPass.price.toFixed(2)
    const chargeAmount = isYearly
        ? plans.paddockPass.yearPrice.toFixed(2)
        : plans.paddockPass.price.toFixed(2)
    const chargePeriod = isYearly ? "year" : "month"

    return (
        <Layout>
            <MetaTags
                title="Paddock Pass Pricing - Open Racer"
                description="Draw up to five racing lines in your browser for free. Paddock Pass adds telemetry imports, more saved lines, and advanced tools."
                location={location}
            />

            <ClientOnly>
                <FirebaseProvider>
                    <SubscriptionStatusChecker
                        onStatusChange={setSubscriptionStatus}
                    />
                </FirebaseProvider>
            </ClientOnly>

            <main className="bg-white text-gray-900">
                <section className="max-w-6xl mx-auto px-4 pt-12 pb-8">
                    {subscriptionStatus === "active" && (
                        <div className="mb-6 rounded-xl border border-green-200 bg-green-50 text-green-900 px-5 py-4">
                            <p className="font-semibold">
                                Your Paddock Pass is active.
                            </p>
                            <p className="text-sm mt-1">
                                Use the button below to manage billing or your
                                subscription.
                            </p>
                        </div>
                    )}

                    {contextMsg && (
                        <div
                            className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-900 px-5 py-4"
                            role="status"
                        >
                            {contextMsg}
                        </div>
                    )}

                    <div className="text-center max-w-3xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-indigo-700 mb-3">
                            Open Racer Paddock Pass
                        </p>
                        <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                            Everything you need to plan and study a faster line
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-3">
                            Draw and save up to five racing lines in this browser for free, without signing in. Paddock Pass unlocks iRacing and Alfano imports, more saved lines, and advanced tools.
                        </p>
                        <p className="text-sm text-gray-500">
                            One product. Choose monthly or yearly billing.
                        </p>
                    </div>

                    <div
                        className="mt-8 grid gap-3 sm:grid-cols-3 max-w-4xl mx-auto"
                        aria-label="Why racers trust Open Racer"
                    >
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-center">
                            <p className="font-semibold text-gray-900">
                                Recognized in 2025
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                Google Maps Platform Awards
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-center">
                            <p className="font-semibold text-gray-900">
                                Used by race coaches
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                Including Racers360 founder Dion von Moltke
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-center">
                            <p className="font-semibold text-gray-900">
                                Secure checkout by Stripe
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                                Card details never touch our servers
                            </p>
                        </div>
                    </div>

                    <div className="mt-8 max-w-4xl mx-auto rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5">
                        <p className="text-center text-sm font-semibold text-indigo-950">
                            See the analyzers in action
                        </p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <a
                                href="/iracing-coaching-report/"
                                className="rounded-xl border border-indigo-200 bg-white px-4 py-3 font-semibold text-indigo-800 no-underline hover:border-indigo-400"
                            >
                                Explore the iRacing analysis
                            </a>
                            <a
                                href="/alfano-coaching-report/"
                                className="rounded-xl border border-indigo-200 bg-white px-4 py-3 font-semibold text-indigo-800 no-underline hover:border-indigo-400"
                            >
                                Explore the Alfano analysis
                            </a>
                        </div>
                    </div>
                </section>

                <section
                    className="max-w-xl mx-auto px-4 py-8"
                    aria-labelledby="paddock-pass-title"
                >
                    <div className="flex justify-center mb-7">
                        <div
                            className="inline-flex items-center bg-gray-100 rounded-xl p-1"
                            aria-label="Billing period"
                        >
                            <button
                                type="button"
                                aria-pressed={!isYearly}
                                onClick={() => setBillingPeriod("monthly")}
                                className={`px-5 py-2.5 rounded-lg transition font-medium ${
                                    !isYearly
                                        ? "bg-white shadow-sm text-gray-900"
                                        : "text-gray-600"
                                }`}
                            >
                                Monthly
                            </button>
                            <button
                                type="button"
                                aria-pressed={isYearly}
                                onClick={() => setBillingPeriod("yearly")}
                                className={`px-5 py-2.5 rounded-lg transition font-medium ${
                                    isYearly
                                        ? "bg-white shadow-sm text-gray-900"
                                        : "text-gray-600"
                                }`}
                            >
                                Yearly{" "}
                                <span className="ml-1 text-green-700 text-sm font-semibold">
                                    Save 25%
                                </span>
                            </button>
                        </div>
                    </div>

                    <div
                        className={`border-2 rounded-2xl p-7 md:p-9 shadow-sm ${
                            subscriptionStatus === "active"
                                ? "border-green-500 bg-green-50/30"
                                : "border-indigo-500"
                        }`}
                    >
                        <div className="flex items-start justify-between gap-4 mb-5">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700 mb-2">
                                    {subscriptionStatus === "active"
                                        ? "Current subscription"
                                        : `${
                                              isYearly ? "Yearly" : "Monthly"
                                          } billing`}
                                </p>
                                <h2 id="paddock-pass-title" className="text-3xl font-bold">Paddock Pass</h2>
                                <p className="text-gray-600 mt-1">
                                    For racers, sim racers, and coaches
                                </p>
                            </div>
                        </div>

                        <div className="mb-2 flex items-end gap-1">
                            <span className="text-5xl font-bold tracking-tight">
                                ${displayPrice}
                            </span>
                            <span className="text-gray-600 pb-1">/month</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-7">
                            {isYearly
                                ? `Billed $${chargeAmount} once per year.`
                                : `Billed $${chargeAmount} each month.`}{" "}
                            No trial period.
                        </p>

                        <ul className="space-y-3 mb-8">
                            {features.map((feature) => (
                                <li key={feature} className="flex items-start">
                                    <CheckIcon />
                                    <span className="text-gray-700">
                                        {feature}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <ClientOnly>
                            <FirebaseProvider>
                                <SubscriptionButton
                                    priceId={
                                        isYearly
                                            ? plans.paddockPass
                                                  .stripePriceIdYear
                                            : plans.paddockPass.stripePriceId
                                    }
                                    buttonText={`Subscribe — $${chargeAmount}/${chargePeriod}`}
                                    checkoutIntent={checkoutIntent}
                                    className="w-full py-3.5 px-5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 transition font-semibold text-lg"
                                />
                            </FirebaseProvider>
                        </ClientOnly>

                        <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                            <p className="font-semibold text-gray-900">
                                Charged ${chargeAmount} today.
                            </p>
                            <p className="mt-1">
                                Secure checkout by Stripe. Cancel anytime from
                                your account. Your existing racing lines stay
                                yours.
                            </p>
                        </div>
                    </div>
                </section>

                <section
                    className="max-w-5xl mx-auto px-4 py-12"
                    aria-labelledby="proof-title"
                >
                    <div className="text-center max-w-2xl mx-auto mb-8">
                        <p className="text-sm font-semibold uppercase tracking-widest text-indigo-700 mb-2">
                            Independent proof
                        </p>
                        <h2 id="proof-title" className="text-3xl font-bold">
                            Built for real track work
                        </h2>
                    </div>
                    <div className="grid gap-6 md:grid-cols-2">
                        <a
                            href="/racing-line-editor"
                            className="block rounded-2xl border border-gray-200 p-6 hover:border-indigo-300 transition no-underline"
                        >
                            <img
                                src="/images/google-maps-platform-awards-2025.png"
                                alt="Google Maps Platform Awards"
                                width="790"
                                height="156"
                                className="w-full h-auto mb-5"
                            />
                            <h3 className="text-xl font-bold text-gray-900">
                                Google Maps Platform Awards 2025
                            </h3>
                            <p className="text-gray-600 mt-2">
                                The Racing Line Editor was recognized for
                                helping motorsport enthusiasts and coaches plan
                                lines with satellite imagery before going to the
                                track.
                            </p>
                            <p className="text-indigo-700 font-semibold mt-4">
                                See the recognized editor
                            </p>
                        </a>

                        <a
                            href="/professional-race-coaches-racing-line-editor"
                            className="block rounded-2xl border border-gray-200 overflow-hidden hover:border-indigo-300 transition no-underline"
                        >
                            <div className="grid grid-cols-3 h-full">
                                <img
                                    src="/images/blog/2021/07/dion-von-moltke.png"
                                    alt="Dion von Moltke"
                                    width="335"
                                    height="255"
                                    className="w-full h-full object-cover"
                                />
                                <div className="col-span-2 p-6">
                                    <p className="text-sm font-semibold uppercase tracking-wide text-indigo-700">
                                        Professional use
                                    </p>
                                    <h3 className="text-xl font-bold text-gray-900 mt-2">
                                        Used by Dion von Moltke
                                    </h3>
                                    <p className="text-gray-600 mt-2">
                                        The professional race coach and
                                        Racers360 founder uses Open Racer to
                                        work on racing lines for tracks around
                                        the world.
                                    </p>
                                    <p className="text-indigo-700 font-semibold mt-4">
                                        Read the story
                                    </p>
                                </div>
                            </div>
                        </a>
                    </div>
                </section>

                <section className="bg-gray-50 border-y border-gray-200">
                    <div className="max-w-5xl mx-auto px-4 py-12">
                        <h2 className="text-3xl font-bold text-center mb-8">
                            What happens after you subscribe
                        </h2>
                        <ol className="grid gap-6 md:grid-cols-3">
                            <li className="bg-white border border-gray-200 rounded-xl p-6">
                                <span className="text-sm font-bold text-indigo-700">
                                    01
                                </span>
                                <h3 className="font-bold text-lg mt-2">
                                    Complete Stripe checkout
                                </h3>
                                <p className="text-gray-600 mt-2">
                                    You see the exact billing amount before
                                    confirming payment.
                                </p>
                            </li>
                            <li className="bg-white border border-gray-200 rounded-xl p-6">
                                <span className="text-sm font-bold text-indigo-700">
                                    02
                                </span>
                                <h3 className="font-bold text-lg mt-2">
                                    Access starts immediately
                                </h3>
                                <p className="text-gray-600 mt-2">
                                    Return to Open Racer and create, duplicate,
                                    or import your racing line.
                                </p>
                            </li>
                            <li className="bg-white border border-gray-200 rounded-xl p-6">
                                <span className="text-sm font-bold text-indigo-700">
                                    03
                                </span>
                                <h3 className="font-bold text-lg mt-2">
                                    Stay in control
                                </h3>
                                <p className="text-gray-600 mt-2">
                                    Manage billing or cancel through your
                                    account. Existing lines remain yours.
                                </p>
                            </li>
                        </ol>
                    </div>
                </section>

                <section
                    className="max-w-3xl mx-auto px-4 py-16"
                    aria-labelledby="pricing-faq-title"
                >
                    <h2
                        id="pricing-faq-title"
                        className="text-3xl font-bold text-center mb-10"
                    >
                        Frequently Asked Questions
                    </h2>
                    <div className="space-y-7">
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                What is included with Paddock Pass?
                            </h3>
                            <p className="text-gray-600">
                                Paddock Pass lets you create and save up to 50
                                racing lines, import iRacing, GoPro, and Alfano
                                telemetry, view unlimited community lines, use
                                advanced analysis tools, and export and
                                privately share your work.
                            </p>
                        </div>
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                When will I be charged?
                            </h3>
                            <p className="text-gray-600">
                                Payment is collected immediately when you
                                complete Stripe checkout. Monthly billing
                                charges $9.99 each month. Yearly billing charges
                                $89.99 once per year. There is no trial period.
                            </p>
                        </div>
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                Can I change or cancel my subscription?
                            </h3>
                            <p className="text-gray-600">
                                Yes. Manage or cancel your subscription at any
                                time from your account through the Stripe
                                Customer Portal.
                            </p>
                        </div>
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                What happens to my racing lines if I cancel?
                            </h3>
                            <p className="text-gray-600">
                                Your racing lines remain yours. You can continue
                                to view and edit existing lines. Your first five
                                manually drawn lines are free; additional creation,
                                duplication, and telemetry imports require an active
                                Paddock Pass.
                            </p>
                        </div>
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                What stays free?
                            </h3>
                            <p className="text-gray-600">
                                Drawing and saving up to five racing lines in
                                your browser is free and does not require an
                                account. Track search, editing lines you own,
                                and viewing up to 10 unique community lines per
                                month also remain free. Paddock Pass is required
                                for Alfano, iRacing, and GoPro telemetry imports.
                            </p>
                        </div>
                        <div className="border-b border-gray-200 pb-7">
                            <h3 className="text-lg font-semibold mb-2">
                                Is my payment information secure?
                            </h3>
                            <p className="text-gray-600">
                                Stripe hosts and processes checkout. Open Racer
                                does not receive or store your full card
                                details.
                            </p>
                        </div>
                    </div>
                </section>
            </main>
        </Layout>
    )
}

export default PricingPage
